import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldCheck, Trash2, Plus } from 'lucide-react';
import { PolicyService, type FleetPolicy, type Stance } from '../../services/policyService';

const STANCES: { value: Stance; label: string; tone: string }[] = [
  { value: 'required', label: 'Required', tone: 'bg-teal-600 text-white' },
  { value: 'preferred', label: 'Preferred', tone: 'bg-green-600 text-white' },
  { value: 'neutral', label: 'Neutral', tone: 'bg-gray-500 text-white' },
  { value: 'prohibited', label: 'Prohibited', tone: 'bg-red-600 text-white' },
];

export interface CatalogEntry {
  slug: string;
  name: string;
}

/**
 * The single home for fleet governance: list, add, set stance, add
 * conditional exceptions, and see live violation impact — integrated
 * into the full report. Reuses the report's practice catalog (adoption).
 */
export const GovernanceSection: React.FC<{
  orgId: string;
  catalog: CatalogEntry[];
  version?: number;
  onChange?: () => void;
}> = ({ orgId, catalog, version, onChange }) => {
  const [policies, setPolicies] = useState<FleetPolicy[]>([]);
  const [impact, setImpact] = useState<Record<string, number>>({});
  const [addSlug, setAddSlug] = useState('');

  const load = useCallback(async () => {
    const pols = await PolicyService.list(orgId);
    setPolicies(pols.data.policies);
    try {
      const comp = await PolicyService.compliance(orgId);
      setImpact(Object.fromEntries(comp.data.byPolicy.map((b) => [b.entitySlug, b.violatedBy])));
    } catch {
      setImpact({});
    }
  }, [orgId]);

  // Reload when the parent signals a change (e.g. a Strengths/Gaps
  // Require/Prohibit button created a policy).
  useEffect(() => {
    load();
  }, [load, version]);

  const governed = useMemo(() => new Set(policies.map((p) => p.entitySlug)), [policies]);
  const addable = catalog.filter((c) => !governed.has(c.slug));
  const nameFor = (slug: string) => catalog.find((c) => c.slug === slug)?.name ?? slug;

  const save = async (policy: FleetPolicy, patch: Partial<FleetPolicy>) => {
    const next = { ...policy, ...patch };
    setPolicies((prev) => prev.map((p) => (p.entitySlug === policy.entitySlug ? next : p)));
    await PolicyService.upsert(orgId, {
      entitySlug: next.entitySlug,
      entityName: next.entityName,
      stance: next.stance,
      satisfiedBy: next.satisfiedBy,
      note: next.note ?? undefined,
    });
    onChange?.();
    void load();
  };

  const addPolicy = async () => {
    if (!addSlug) return;
    await PolicyService.upsert(orgId, {
      entitySlug: addSlug,
      entityName: nameFor(addSlug),
      stance: 'preferred',
    });
    setAddSlug('');
    onChange?.();
    await load();
  };

  const remove = async (slug: string) => {
    await PolicyService.remove(orgId, slug);
    setPolicies((prev) => prev.filter((p) => p.entitySlug !== slug));
    onChange?.();
    void load();
  };

  return (
    <section className="p-5 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-center gap-2.5 mb-1">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-100 dark:bg-teal-900/30">
          <ShieldCheck className="w-4 h-4 text-teal-600 dark:text-teal-400" />
        </span>
        <h2 className="font-semibold text-gray-900 dark:text-white">Fleet policies</h2>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Require or prohibit practices across the fleet. A required practice can be satisfied by an
        alternative — e.g. "Dependabot required, unless Renovate is present". Violations update
        live.
      </p>

      <div className="mb-4 flex items-center gap-2">
        <select
          value={addSlug}
          onChange={(e) => setAddSlug(e.target.value)}
          className="flex-1 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-transparent text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500/40"
        >
          <option value="">Add a practice to govern…</option>
          {addable.map((c) => (
            <option key={c.slug} value={c.slug}>
              {c.name}
            </option>
          ))}
        </select>
        <button
          onClick={addPolicy}
          disabled={!addSlug}
          className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-teal-700 hover:shadow disabled:opacity-40 disabled:hover:bg-teal-600 disabled:hover:shadow-sm"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      {policies.length === 0 ? (
        <p className="text-sm text-gray-400">No policies yet — add a practice above.</p>
      ) : (
        <ul className="space-y-3">
          {policies.map((p) => (
            <li
              key={p.entitySlug}
              className="p-4 rounded-xl border border-gray-200/70 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20"
            >
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">{p.entityName}</span>
                  {(impact[p.entitySlug] ?? 0) > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                      violated by {impact[p.entitySlug]} repos
                    </span>
                  )}
                </div>
                <button
                  onClick={() => remove(p.entitySlug)}
                  className="rounded-lg p-1.5 text-gray-400 transition-colors hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"
                  title="Remove policy"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="flex flex-wrap gap-2 mb-2">
                {STANCES.map((st) => (
                  <button
                    key={st.value}
                    onClick={() => save(p, { stance: st.value })}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors active:scale-[.97] ${
                      p.stance === st.value
                        ? `${st.tone} shadow-sm`
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    {st.label}
                  </button>
                ))}
              </div>

              {p.stance === 'required' && (
                <div className="mb-2">
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    …unless one of these is present:
                  </label>
                  <select
                    multiple
                    value={p.satisfiedBy}
                    onChange={(e) =>
                      save(p, { satisfiedBy: Array.from(e.target.selectedOptions, (o) => o.value) })
                    }
                    className="w-full px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-transparent text-sm text-gray-900 dark:text-white h-20 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                  >
                    {catalog
                      .filter((c) => c.slug !== p.entitySlug)
                      .map((c) => (
                        <option key={c.slug} value={c.slug}>
                          {c.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <input
                defaultValue={p.note ?? ''}
                onBlur={(e) =>
                  e.target.value !== (p.note ?? '') && save(p, { note: e.target.value })
                }
                placeholder="Note / rationale (optional)"
                className="w-full px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent text-sm text-gray-700 dark:text-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
